import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  alpha,
  ButtonBase,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import KitchenIcon from "@mui/icons-material/Kitchen";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import TuneIcon from "@mui/icons-material/Tune";
import HotelIcon from "@mui/icons-material/Hotel";
import { toast } from "react-toastify";
import CompanyConfigurationServices from "../../services/CompanyConfigurationService";
import { useUserWithCompany } from "hooks/useCompany";

const settingsCards = [
  {
    id: "configuration",
    title: "Company Configuration",
    description:
      "Centralize order types, print behavior, and company structure controls in one place",
    icon: <TuneIcon sx={{ fontSize: 64 }} />,
    path: "/settings/configuration",
    color: "#0f766e",
    features: [
      "Order Settings",
      "Print Settings",
      "Company Structure",
      "Operational Modules",
    ],
  },
  {
    id: "company",
    title: "Company Settings",
    description:
      "Customize company information, branding, business details, and organization settings",
    icon: <BusinessIcon sx={{ fontSize: 64 }} />,
    path: "/settings/company",
    color: "#8b5cf6",
    features: [
      "Company Information",
      "Business Details",
      "Branding & Logo",
      "Contact Information",
    ],
  },
  {
    id: "users",
    title: "User Management",
    description:
      "Manage staff accounts, assign roles, and control access levels for your restaurant team",
    icon: <PeopleIcon sx={{ fontSize: 64 }} />,
    path: "/settings/users",
    color: "#0891b2",
    features: [
      "Employee Accounts",
      "Role Assignment",
      "Access Control",
      "Staff Status Management",
    ],
  },
  {
    id: "service",
    title: "QR Settings",
    description:
      "Manage tables, seating arrangements, and service area configurations",
    icon: <TableRestaurantIcon sx={{ fontSize: 64 }} />,
    path: "/settings/service",
    color: "#FF6600",
    features: [
      "Table Management",
      "Seating Arrangements",
      "Service Areas",
      "Table Status",
    ],
  },
  {
    id: "menu",
    title: "Menu Settings",
    description:
      "Configure categories, items, variations, modifiers, and complete menu management",
    icon: <RestaurantMenuIcon sx={{ fontSize: 64 }} />,
    path: "/settings/menu",
    color: "#10b981",
    features: [
      "Categories & Sub Categories",
      "Menu Items & Variations",
      "Item Variations",
      "Modifiers & Options",
    ],
  },

  {
    id: "kitchen",
    title: "Kitchen Settings",
    description:
      "Manage kitchen operations, stations, order routing, and display preferences",
    icon: <KitchenIcon sx={{ fontSize: 64 }} />,
    path: "/settings/kitchen",
    color: "#f97316",
    features: [
      "Kitchen Stations",
      "Order Routing Rules",
      "Display Preferences",
      "Printer Configuration",
    ],
  },

  {
    id: "promotions",
    title: "Promotions Settings",
    description:
      "Create and manage offers, special deals, and discount programs",
    icon: <LocalOfferIcon sx={{ fontSize: 64 }} />,
    path: "/settings/promotions",
    color: "#ec4899",
    features: [
      "Special Offers",
      "Offer Items",
      "Discount Programs",
      "Promotion Campaigns",
    ],
  },
  {
    id: "guest-portal",
    title: "Guest Portal",
    description:
      "What guests see after scanning a room or table QR — food, room service, buffet, and hotel help",
    icon: <HotelIcon sx={{ fontSize: 64 }} />,
    path: "/settings/guest-portal",
    color: "#0F3D30",
    features: [
      "Hub modules",
      "Wi-Fi & checkout",
      "Buffet hours",
      "Room request catalogs",
    ],
  },
  {
    id: "guest-hub-admin",
    title: "Guest Hub Board",
    description:
      "Live room board and guest request queue — housekeeping, room service, concierge, and more",
    icon: <HotelIcon sx={{ fontSize: 64 }} />,
    path: "/guest-hub-admin",
    color: "#0f5c4c",
    features: [
      "Room board",
      "Request queue",
      "DND & make-up",
      "Status updates",
    ],
  },

];

const SettingsHub = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId } = useUserWithCompany();
  const isDark = theme.palette.mode === "dark";
  const [isTableSettingsEnabled, setIsTableSettingsEnabled] = useState(true);

  useEffect(() => {
    if (!companyId) return;

    CompanyConfigurationServices.getConfigurations(companyId, "order_settings")
      .then((response) => {
        if (!response?.success) return;

        const items =
          response?.data?.view?.data ??
          response?.data?.view?.items ??
          response?.data?.items ??
          [];

        if (items.length > 0) {
          const configValue = items[0]?.value || {};
          setIsTableSettingsEnabled(configValue.Dinein !== false);
        }
      })
      .catch((error) => {
        console.error("Error fetching order settings for Settings Hub:", error);
      });
  }, [companyId]);

  const handleCardClick = (path, isDisabled) => {
    if (isDisabled) {
      toast.info("QR Settings is disabled. Enable Dine-in in Company Configuration first.");
      return;
    }
    navigate(path);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: { xs: "flex-start", md: "center" },
        p: { xs: 2, md: 2 },
        pt: { xs: 3, md: 2 },
        pb: { xs: 12, md: 2 },
      }}
    >
      {/* Page Header */}
      <Stack spacing={1} sx={{ mb: { xs: 3, md: 4 }, textAlign: "center", maxWidth: 800 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontSize: { xs: "1.9rem", md: "3rem" },
            letterSpacing: "-0.5px",
          }}
        >
          Restaurant Settings
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "1rem",
            lineHeight: 1.6,
            display: { xs: "none", sm: "block" },
          }}
        >
          Choose a settings category to configure your restaurant operations
        </Typography>
      </Stack>

      {/* Settings Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(2, 1fr)",
            xl: "repeat(3, 1fr)",
          },
          gap: { xs: 2, md: 3 },
          maxWidth: 1400,
          width: "100%",
          gridAutoRows: { xs: "auto", md: "1fr" },
          flex: { xs: "none", md: 1 },
        }}
      >
        {settingsCards.map((card) => {
          const isDisabled = card.id === "service" && !isTableSettingsEnabled;

          return (
            <ButtonBase
              key={card.id}
              onClick={() => handleCardClick(card.path, isDisabled)}
              disabled={isDisabled}
              sx={{
                borderRadius: "20px",
                textAlign: "left",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                opacity: isDisabled ? 0.55 : 1,
                "&:hover": {
                  transform: isDisabled ? "none" : "translateY(-8px)",
                },
              }}
            >
              <Card
                elevation={0}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "20px",
                  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"
                    }`,
                  background: isDark
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  overflow: "hidden",
                  position: "relative",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    border: `1px solid ${alpha(card.color, 0.5)}`,
                    boxShadow: `0 20px 60px ${alpha(card.color, 0.15)}`,
                    background: isDark
                      ? alpha(card.color, 0.05)
                      : alpha(card.color, 0.03),
                    "& .icon-box": {
                      transform: "scale(1.1) rotate(5deg)",
                      background: `linear-gradient(135deg, ${alpha(
                        card.color,
                        0.2,
                      )}, ${alpha(card.color, 0.1)})`,
                    },
                    "& .feature-item": {
                      transform: "translateX(4px)",
                    },
                  },
                  ...(isDisabled && {
                    filter: "grayscale(0.2)",
                    "&:hover": {
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
                      boxShadow: "none",
                      background: isDark
                        ? "rgba(255, 255, 255, 0.02)"
                        : "rgba(255, 255, 255, 0.6)",
                    },
                  }),
                }}
              >
                <CardContent
                  sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <Stack spacing={2.5} sx={{ flex: 1 }}>
                    {/* Icon, Title & Description Row */}
                    <Box
                      sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                    >
                      {/* Icon */}
                      <Box
                        className="icon-box"
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          borderRadius: "16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: `linear-gradient(135deg, ${alpha(
                            card.color,
                            0.15,
                          )}, ${alpha(card.color, 0.05)})`,
                          color: card.color,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {card.icon}
                      </Box>

                      {/* Title & Description */}
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        {/* Card Category Label */}
                        {/* <Typography
                        variant="overline"
                        sx={{
                          color: card.color,
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          letterSpacing: "1px",
                        }}
                      >
                        {card.id.toUpperCase()}
                      </Typography> */}

                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.5,
                            fontSize: "0.85rem",
                          }}
                        >
                          {card.description}
                        </Typography>
                        {isDisabled && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.warning.main,
                              fontWeight: 700,
                              mt: 0.5,
                              display: "block",
                            }}
                          >
                            Disabled: Turn on Dine-in in Company Configuration
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Features List */}
                    <Stack spacing={1.2}>
                      {card.features.map((feature, index) => (
                        <Box
                          key={index}
                          className="feature-item"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            transition: "transform 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: card.color,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                              fontSize: "0.8rem",
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>

                {/* Decorative Corner */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(
                      card.color,
                      0.1,
                    )}, transparent)`,
                    pointerEvents: "none",
                  }}
                />
              </Card>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default SettingsHub;
